/**
 * OnboardingModal — BeHeard's first-run onboarding as a pop-up over the real
 * app (owner decision 2026-10-09). OnboardingGuard renders the app shell behind
 * it (inert, aria-hidden) and this dialog on top.
 *
 *   Welcome → Goal → Experience → Role → You're all set
 *
 * - No X, Escape or click outside closes it, and there is no exit link (no
 *   "Back to LevelUp"): the user stays until done.
 * - The answers are the existing ones and are saved with the existing
 *   POST /onboarding/me (it builds the assessment path; no setup step).
 * - "Started" (Get Started) is recorded on the server as before
 *   (POST /onboarding/me/start), so a refresh resumes at the questions.
 * - A failed save keeps the answers; the error shows above the footer with Retry.
 * - Every question offers "I'll do this later" (owner decision 2026-10-10);
 *   rules in utils/onboardingModalFlow. Skips are remembered in this browser
 *   (QUESTIONS_KEY) so a refresh does not ask them again.
 * - Layering and fit: the overlay is portalled to document.body above every
 *   shell and app layer (ui-shell header 100, nav 200, BeHeard's z-[9999]
 *   layers and toasts, ui-shell's dev theme panel 99999), so no app ancestor
 *   can trap it and the navbar never paints over it. The card is never taller
 *   than the screen: the brand band and the footer stay pinned and only the
 *   step body scrolls.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Check, Loader2 } from 'lucide-react';
import beheardIcon from '../../assets/beheard-icon.png';
import {
  onboardingApi,
  useGetOnboardingProfileQuery,
  useSaveOnboardingProfileMutation,
} from '../../store/api/onboardingApi';
import type { AppDispatch } from '../../store';
import {
  markOnboardingStarted,
  needsOnboardingMarkerUpgrade,
  onboardingStartRecorded,
  readStoredOnboardingMarker,
  recordGetStarted,
  selectOnboardingUserId,
  selectOnboardingWorkspaceId,
  serverOnboardingStart,
  startedThisSession,
  upgradeStoredOnboardingMarker,
} from '../../utils/onboardingWelcome';
import {
  MODAL_STEPS,
  OPTIONAL_STEPS,
  QUESTION_STEPS,
  canContinue,
  initialModalState,
  modalKey,
  modalReducer,
  profileBody,
  toStoredQuestions,
  type ModalState,
  type StoredQuestions,
} from '../../utils/onboardingModalFlow';
import { EXPERIENCE_OPTIONS, INTENT_OPTIONS, type OnboardingOption } from './onboardingQuestions';

interface OnboardingModalProps {
  /** "Open BeHeard" on the done step, after the fade-out. */
  onFinished: () => void;
  /** Test seam: the state to open with (defaults to welcome, or the questions once started). */
  initialState?: ModalState;
}

/** Questions skipped in this browser, per user + workspace (nothing is saved for a skip until the end). */
export const QUESTIONS_KEY = (userId: string, workspaceId: string) => `levelup.beheard.onboardingQuestions:${userId}:${workspaceId}`;

function readStoredQuestions(identity: { userId: string; workspaceId: string } | null): StoredQuestions | null {
  if (!identity) return null;
  try {
    const raw = JSON.parse(localStorage.getItem(QUESTIONS_KEY(identity.userId, identity.workspaceId)) ?? 'null') as StoredQuestions | null;
    if (!raw || typeof raw !== 'object') return null;
    const intent = raw.intent === 'skipped' || INTENT_OPTIONS.some((o) => o.value === raw.intent) ? raw.intent : undefined;
    const experience = raw.experience === 'skipped' || EXPERIENCE_OPTIONS.some((o) => o.value === raw.experience) ? raw.experience : undefined;
    return { intent, experience };
  } catch {
    return null;
  }
}

function writeStoredQuestions(identity: { userId: string; workspaceId: string } | null, q: StoredQuestions | null): void {
  if (!identity) return;
  try {
    const key = QUESTIONS_KEY(identity.userId, identity.workspaceId);
    if (q) localStorage.setItem(key, JSON.stringify(q));
    else localStorage.removeItem(key);
  } catch {
    /* storage unavailable: a refresh asks the skipped question again */
  }
}

/** Above every shell and app layer (see the header comment). */
export const ONBOARDING_OVERLAY_Z = 'z-[100000]';
/** Never taller than the screen (100vh fallback where dvh is unsupported). */
export const ONBOARDING_CARD_MAX_H = 'max-h-[calc(100vh-2rem)] supports-[height:100dvh]:max-h-[calc(100dvh-2rem)]';

/** BeHeard's brand (Central ui-shell theme `shellBackground`) and a darker shade for the band gradient. */
export const BRAND = '#3b1f74';
const BRAND_DARK = '#28134f';

const STYLE = `
@keyframes bhOnbIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes bhOnbCheck { from { transform: scale(.4); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.bh-onb-step { animation: bhOnbIn 180ms ease-out; }
.bh-onb-check { animation: bhOnbCheck 200ms ease-out; }
.bh-onb-fade { transition: opacity 200ms ease-out; }
@media (prefers-reduced-motion: reduce) {
  .bh-onb-step, .bh-onb-check { animation: none !important; }
  .bh-onb-fade, .bh-onb-bar { transition: none !important; }
}`;

const TITLES: Record<ModalState['step'], { title: string; helper: string }> = {
  welcome: { title: 'Be the expert people trust', helper: 'Takes about 1 minute.' },
  goal: { title: 'What do you want to win?', helper: 'Your practice path will aim right at it.' },
  experience: { title: 'Where are you starting from?', helper: 'So every session stretches you, not stalls you.' },
  role: { title: 'Where do you want to go?', helper: 'Optional. Practice that fits your next step.' },
  done: { title: "You're ready to be heard", helper: 'Here is what you get now.' },
};

export function OnboardingModal({ onFinished, initialState }: OnboardingModalProps) {
  const dispatchStore = useDispatch<AppDispatch>();
  const { data: onboardingProfile } = useGetOnboardingProfileQuery();
  const [saveProfile] = useSaveOnboardingProfileMutation();

  // "Started" exactly as the old Welcome decided it (server first; the O4
  // local marker only while the server cannot say).
  const userId = useSelector(selectOnboardingUserId);
  const workspaceId = useSelector(selectOnboardingWorkspaceId);
  const identity = userId && workspaceId ? { userId, workspaceId } : null;
  const serverStart = serverOnboardingStart(onboardingProfile);
  const storedMarker = readStoredOnboardingMarker(identity);
  // Unknown identity: the questions directly, as the gate's wizard did.
  const started =
    identity === null ||
    serverStart === 'started' ||
    (identity !== null &&
      onboardingStartRecorded({ server: serverStart, storedMarker, startedThisSession: startedThisSession(identity) }));

  const [state, dispatch] = useReducer(
    modalReducer,
    undefined,
    () => initialState ?? initialModalState(started, started ? readStoredQuestions(identity) : null),
  );

  // Remember skips for a refresh; forget them once saved.
  const storedQuestions = state.step === 'done' ? null : toStoredQuestions(state);
  const storedJson = storedQuestions ? JSON.stringify(storedQuestions) : null;
  useEffect(() => {
    if (!userId || !workspaceId) return;
    writeStoredQuestions({ userId, workspaceId }, storedJson ? (JSON.parse(storedJson) as StoredQuestions) : null);
  }, [userId, workspaceId, storedJson]);
  const [leaving, setLeaving] = useState(false);

  const postStart = useCallback(async () => {
    const request = dispatchStore(onboardingApi.endpoints.startOnboarding.initiate());
    try {
      const recorded = await request.unwrap();
      dispatchStore(
        onboardingApi.util.updateQueryData('getOnboardingProfile', undefined, (draft) => {
          draft.startedAt = recorded.startedAt;
          draft.startedAtKnown = true;
        }),
      );
      return recorded;
    } finally {
      request.reset();
    }
  }, [dispatchStore]);

  // One-time upgrade of an old (O4) marker the server knows nothing about.
  const upgradeMarker =
    identity !== null && onboardingProfile !== undefined && needsOnboardingMarkerUpgrade({ server: serverStart, storedMarker });
  useEffect(() => {
    if (!upgradeMarker || !userId || !workspaceId) return;
    void upgradeStoredOnboardingMarker({ identity: { userId, workspaceId }, postStart });
  }, [upgradeMarker, userId, workspaceId, postStart]);

  const getStarted = () => {
    dispatch({ type: 'start' });
    if (identity) {
      void recordGetStarted({ identity, server: serverStart, postStart, markLocal: () => markOnboardingStarted(identity) });
    }
  };

  const submit = async (over: Partial<ModalState> = {}) => {
    const s = { ...state, ...over };
    if (s.step !== 'role') return;
    dispatch({ type: 'saveStarted' });
    try {
      // A skipped question saves its default (profileBody).
      await saveProfile(profileBody(s)).unwrap();
      dispatch({ type: 'saveSucceeded' });
    } catch (err: unknown) {
      const message = (err as { data?: { message?: string } } | null)?.data?.message;
      dispatch({ type: 'saveFailed', message: message || "We couldn't save your answers. Please try again." });
    }
  };

  /** "I'll do this later": on without an answer; on the last question (role), save without a role. */
  const later = () => {
    if (state.saving) return;
    dispatch({ type: 'skip' });
    if (state.step === 'role') void submit({ currentRole: '', targetRole: '' });
  };

  const finish = () => {
    setLeaving(true);
    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    setTimeout(onFinished, reduced ? 0 : 200);
  };

  const primary = () => {
    if (state.step === 'welcome') return getStarted();
    if (state.step === 'role') return void submit();
    if (state.step === 'done') return finish();
    dispatch({ type: 'continue' });
  };

  // Focus: trapped in the dialog; starts on the first option (or the primary button).
  const dialogRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = dialogRef.current;
    if (!root) return;
    // Each step opens at the top of the (scrolling) body.
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    const first =
      root.querySelector<HTMLElement>('[data-onb-option][aria-checked="true"]') ??
      root.querySelector<HTMLElement>('[data-onb-option], input, [data-onb-primary]');
    first?.focus();
  }, [state.step]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const root = dialogRef.current;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      dispatch({ type: 'dismissAttempt' });
      return;
    }
    if (e.key === 'Tab' && root) {
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input, [tabindex]:not([tabindex="-1"])'),
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
      return;
    }
    const k = modalKey(e.key);
    if (k === 'continue') {
      const target = e.target as HTMLElement;
      if (target.tagName === 'A' || (target.tagName === 'BUTTON' && !target.hasAttribute('data-onb-option'))) return;
      if (state.step !== 'done' && !canContinue(state)) return;
      e.preventDefault();
      primary();
    } else if ((k === 'next-option' || k === 'prev-option') && root) {
      const options = Array.from(root.querySelectorAll<HTMLElement>('[data-onb-option]'));
      const i = options.indexOf(document.activeElement as HTMLElement);
      if (options.length === 0 || i < 0) return;
      e.preventDefault();
      const next = options[(i + (k === 'next-option' ? 1 : options.length - 1)) % options.length];
      next.focus();
      next.click();
    }
  };

  const stepIndex = MODAL_STEPS.indexOf(state.step);
  const total = MODAL_STEPS.length;
  const { title, helper } = TITLES[state.step];
  const optional = OPTIONAL_STEPS.has(state.step) || QUESTION_STEPS.has(state.step);
  const continueLabel =
    state.step === 'welcome' ? 'Get started' : state.step === 'done' ? 'Open BeHeard' : state.error ? 'Retry' : 'Continue';

  const overlay = (
    <div
      className={`bh-onb-fade fixed inset-0 ${ONBOARDING_OVERLAY_Z} overflow-y-auto bg-gray-900/45 backdrop-blur-sm ${leaving ? 'opacity-0' : 'opacity-100'}`}
      data-testid="onboarding-overlay"
      onClick={(e) => {
        // A click outside never closes the pop-up.
        if (!dialogRef.current?.contains(e.target as Node)) dispatch({ type: 'dismissAttempt' });
      }}
    >
      <style>{STYLE}</style>
      {/* Centred when it fits; a bottom sheet on phones. Tiny screens scroll the overlay. */}
      <div className="flex min-h-full flex-col items-center pt-4 sm:p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="onboarding-modal-title"
          data-testid="onboarding-modal"
          data-step={state.step}
          onKeyDown={onKeyDown}
          style={{ '--brand': BRAND, '--brand-dark': BRAND_DARK, '--brand-tint': `${BRAND}12`, '--brand-line': `${BRAND}33` } as CSSProperties}
          className={`mt-auto sm:mb-auto flex w-full sm:max-w-[600px] flex-col overflow-hidden bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl ${ONBOARDING_CARD_MAX_H}`}
        >
          {/* Brand band: BeHeard's colour, its real icon, the name, step and progress. Always visible. */}
          <div
            data-testid="onboarding-brand-band"
            className="flex-none px-6 sm:px-8 pt-6 pb-5 text-white"
            style={{ background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)` }}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="w-14 h-14 rounded-2xl bg-white p-1 shadow-sm flex items-center justify-center">
                  <img src={beheardIcon} alt="BeHeard" data-testid="onboarding-app-icon" className="w-12 h-12" />
                </span>
                <span className="text-lg font-semibold text-white">BeHeard</span>
              </div>
              <span className="text-xs font-medium text-white/90" data-testid="onboarding-step-count">
                Step {stepIndex + 1} of {total}
              </span>
            </div>
            <div className="mt-5 h-1 w-full rounded-full bg-white/25 overflow-hidden" aria-hidden="true">
              <div
                className="bh-onb-bar h-full bg-white transition-[width] duration-200 ease-out"
                style={{ width: `${((stepIndex + 1) / total) * 100}%` }}
              />
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col" style={{ backgroundColor: `${BRAND}0a` }}>
            {/* The step body is the only part that scrolls. */}
            <div ref={bodyRef} data-testid="onboarding-body" className="min-h-0 flex-1 overflow-y-auto px-6 sm:px-8 pt-6 sm:pt-8 pb-2">
              <div key={state.step} className="bh-onb-step">
                {state.step === 'done' ? (
                  <div className="bh-onb-check w-12 h-12 rounded-full bg-[var(--brand)] text-white flex items-center justify-center mb-4" aria-hidden="true">
                    <Check className="w-6 h-6" />
                  </div>
                ) : null}
                <h2 id="onboarding-modal-title" className="text-2xl font-semibold text-gray-900">
                  {title}
                </h2>
                <p className="mt-1 text-gray-600">{helper}</p>

                <div className="mt-6">
                  {state.step === 'welcome' && (
                    <p className="text-gray-700" data-testid="onboarding-welcome">
                      Practise the conversations that matter and get honest feedback on every one, so you walk in ready.
                    </p>
                  )}
                  {state.step === 'goal' && (
                    <OptionGrid
                      name="Main goal"
                      options={INTENT_OPTIONS}
                      selected={state.intent}
                      onSelect={(value) => dispatch({ type: 'selectIntent', value })}
                    />
                  )}
                  {state.step === 'experience' && (
                    <OptionGrid
                      name="Experience level"
                      options={EXPERIENCE_OPTIONS}
                      selected={state.experience}
                      onSelect={(value) => dispatch({ type: 'selectExperience', value })}
                    />
                  )}
                  {state.step === 'role' && (
                    <div className="space-y-4">
                      <label className="block">
                        <span className="block text-sm font-medium text-gray-700 mb-1">Current role (optional)</span>
                        <input
                          type="text"
                          className="input w-full"
                          placeholder="e.g., Senior Software Engineer"
                          value={state.currentRole}
                          onChange={(e) => dispatch({ type: 'setRole', field: 'currentRole', value: e.target.value })}
                        />
                      </label>
                      <label className="block">
                        <span className="block text-sm font-medium text-gray-700 mb-1">Target role (optional)</span>
                        <input
                          type="text"
                          className="input w-full"
                          placeholder="e.g., Engineering Manager"
                          value={state.targetRole}
                          onChange={(e) => dispatch({ type: 'setRole', field: 'targetRole', value: e.target.value })}
                        />
                        <span className="block text-xs text-gray-600 mt-1">If you&apos;re preparing for a new position</span>
                      </label>
                    </div>
                  )}
                  {state.step === 'done' && (
                    <ul className="space-y-2 text-gray-700" data-testid="onboarding-summary">
                      {state.intent && (
                        <li className="flex gap-2"><Check className="w-5 h-5 text-[var(--brand)] shrink-0" aria-hidden="true" />Working toward: {INTENT_OPTIONS.find((o) => o.value === state.intent)?.label}</li>
                      )}
                      {state.experience && (
                        <li className="flex gap-2"><Check className="w-5 h-5 text-[var(--brand)] shrink-0" aria-hidden="true" />Starting from: {EXPERIENCE_OPTIONS.find((o) => o.value === state.experience)?.label}</li>
                      )}
                      <li className="flex gap-2"><Check className="w-5 h-5 text-[var(--brand)] shrink-0" aria-hidden="true" />Your practice path is waiting on your dashboard</li>
                    </ul>
                  )}
                </div>
              </div>
            </div>

            {/* Footer: always visible. */}
            <div data-testid="onboarding-footer" className="flex-none px-6 sm:px-8 pt-4 pb-6 sm:pb-8">
              {state.error && (
                <div role="alert" className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="onboarding-error">
                  {state.error}
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <div>
                  {(state.step === 'experience' || state.step === 'role') && (
                    <button
                      type="button"
                      onClick={() => dispatch({ type: 'back' })}
                      disabled={state.saving}
                      className="text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] rounded disabled:opacity-50"
                    >
                      Back
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  {optional && (
                    <button
                      type="button"
                      onClick={later}
                      disabled={state.saving}
                      className="text-sm text-gray-600 hover:text-gray-900 underline disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] rounded"
                      data-testid="onboarding-later"
                    >
                      I&apos;ll do this later
                    </button>
                  )}
                  <button
                    type="button"
                    data-onb-primary=""
                    data-testid="onboarding-continue"
                    onClick={primary}
                    disabled={state.step !== 'done' && !canContinue(state)}
                    className="inline-flex items-center gap-2 rounded-lg bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white font-medium px-5 py-2.5 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2"
                  >
                    {state.saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                    {state.saving ? 'Creating your path...' : continueLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Portalled to <body>: outside the (inert) app root, above every app and shell layer.
  return typeof document === 'undefined' ? overlay : createPortal(overlay, document.body);
}


function OptionGrid<V extends string>({
  name,
  options,
  selected,
  onSelect,
}: {
  name: string;
  options: OnboardingOption<V>[];
  selected: V | null;
  onSelect: (v: V) => void;
}) {
  return (
    <div role="radiogroup" aria-label={name} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {options.map((o, i) => {
        const on = selected === o.value;
        const Icon = o.icon;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            data-onb-option=""
            tabIndex={on || (selected === null && i === 0) ? 0 : -1}
            onClick={() => onSelect(o.value)}
            className={`relative text-left p-4 rounded-xl border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 ${
              on ? 'border-[var(--brand)] bg-[var(--brand-tint)]' : 'border-[var(--brand-line)] bg-white hover:border-[var(--brand)]'
            }`}
          >
            {on && (
              <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[var(--brand)] text-white flex items-center justify-center" aria-hidden="true">
                <Check className="w-3 h-3" />
              </span>
            )}
            <Icon className={`w-6 h-6 ${on ? 'text-[var(--brand)]' : 'text-gray-600'}`} aria-hidden="true" />
            <div className="mt-2 font-medium text-gray-900">{o.label}</div>
            <div className="text-sm text-gray-600 mt-0.5">{o.description}</div>
          </button>
        );
      })}
    </div>
  );
}
